import performance from './performance';
import { BeaconInstance, JsonData } from '../types';
import { EVENT_CODES, REPORT_HOST } from './constrants';
import { performanceReportDefered } from '../utils/helpers';

interface HttpRequestInitOptions {
  performanceServer: boolean;
  errorServer: boolean;
}

// 是否为上报请求
const isReportRequest = (url: string) => url.indexOf(REPORT_HOST) > -1;

class RequestTemplate {
  public method: string;

  public duration: number;

  public responseStatus: string;

  public triggerTime: number;

  public href: string;

  constructor(config: JsonData = {}) {
    const { method, duration, responseStatus, triggerTime, href } = config;
    this.method = method;
    this.duration = duration;
    this.responseStatus = responseStatus;
    this.triggerTime = triggerTime;
    this.href = href;
  }
}

export default class HttpRequestReport {
  private readonly beacon: BeaconInstance;

  constructor(beacon: BeaconInstance) {
    this.beacon = beacon;
  }

  /**
   * fetch请求拦截
   */
  private interceptFetch(performanceServer, errorServer) {
    const nativeFetch = window.fetch;
    if (!nativeFetch) {
      return;
    }

    const beacon = this.beacon;
    window.fetch = function traceFetch(target, options = {}) {
      const fetchStart = Date.now();
      const { method = 'GET' } = options;
      const result = nativeFetch(target, options);
      const href = window.location.href;
      result.then(
        (res) => {
          const { url, status, statusText } = res;
          const isNeedSkipReport = isReportRequest(url);

          if ((status >= 200 && status < 300) || status === 304) {
            const duration = Date.now() - fetchStart;
            if (performanceServer && !isNeedSkipReport) {
              const result = performance.tracePerformance('server', {
                requestMethod: method,
                src: url,
                responseStatus: status,
                requestDuration: duration,
                href,
              });
              performanceReportDefered.ready().then(() => {
                beacon.onUserAction(EVENT_CODES.SERVER_EVENT_XHR_LOAD, result);
              });
            }
          } else if (errorServer && !isNeedSkipReport) {
            const duration = Date.now() - fetchStart;
            performanceReportDefered.ready().then(() => {
              beacon.onUserAction(EVENT_CODES.SERVER_EVENT_XHR_ERROR, {
                requestMethod: method,
                src: url,
                responseStatus: status,
                requestDuration: duration,
                href,
                statusText,
              });
            });
          }
        },
        (e) => {
          const url = typeof target === 'string' ? target : target.url;
          const isNeedSkipReport = isReportRequest(url);
          if (!isNeedSkipReport) {
            const duration = Date.now() - fetchStart;
            // 无法发起请求,连接失败
            performanceReportDefered.ready().then(() => {
              beacon.onUserAction(EVENT_CODES.SERVER_EVENT_XHR_ERROR, {
                requestMethod: method,
                src: target,
                errorMessage: e.message,
                href,
                requestDuration: duration,
              });
            });
          }
        }
      );
      return result;
    };
  }

  /**
   * ajax, axios请求拦截
   */
  private interceptAjax(performanceServer, errorServer) {
    const { open, send } = XMLHttpRequest.prototype;
    const _config = new Map();
    const beancon = this.beacon;

    // 劫持 open方法
    // @ts-ignore
    XMLHttpRequest.prototype.open = function openXHR(method, url, async) {
      const rt = new RequestTemplate();
      _config.set(url, rt);
      rt.method = method;
      rt.triggerTime = Date.now();
      rt.href = window.location.href; // 请求发起的页面
      return open.call(this, method, url, async);
    };

    // 劫持 send方法
    XMLHttpRequest.prototype.send = function (body) {
      // body 就是post方法携带的参数
      // readyState发生改变时触发,也就是请求状态改变时
      // readyState 会依次变为 2,3,4 也就是会触发三次这里
      this.addEventListener('readystatechange', () => {
        const { readyState, status, responseURL: resUrl, responseType } = this;
        const rt = _config.get(resUrl);
        const isNeedSkipReport = isReportRequest(resUrl); // 是否需要跳过上报
        const needReportBody = typeof body === 'string' && body.length <= 1024 * 4; // 是否需要上报参数
        const params = needReportBody ? body : 'NOT_NEED_REPORT';
        const needReportResponse = responseType === 'text' && this.responseText.length < 1024 * 2; // 是否需要上报响应体
        const responseText = needReportResponse ? this.responseText : 'NOT_NEED_REPORT';

        if (readyState === 4) {
          const duration = Date.now() - rt?.triggerTime;
          // 请求已完成,且响应已就绪
          if ((status >= 200 && status < 300) || status === 304) {
            if (rt && performanceServer && !isNeedSkipReport) {
              const result = performance.tracePerformance('server', {
                requestMethod: rt?.method,
                src: resUrl,
                responseStatus: status,
                requestDuration: duration,
                href: rt?.href,
                body: params,
              });
              performanceReportDefered.ready().then(() => {
                beancon.onUserAction(EVENT_CODES.SERVER_EVENT_XHR_LOAD, result);
              });
            }
          } else if (rt && errorServer && !isNeedSkipReport) {
            performanceReportDefered.ready().then(() => {
              beancon.onUserAction(EVENT_CODES.SERVER_EVENT_XHR_ERROR, {
                requestMethod: rt?.method,
                src: resUrl,
                responseStatus: status,
                requestDuration: duration,
                responseText,
                href: rt?.href,
                body: params,
              });
            });
          }
          _config.delete(resUrl);
        }
      });
      return send.call(this, body);
    };
  }

  public init({ performanceServer, errorServer }: HttpRequestInitOptions) {
    if (!performanceServer && !errorServer) {
      return;
    }
    this.interceptAjax(performanceServer, errorServer);
    this.interceptFetch(performanceServer, errorServer);
  }
}
